import { AddressZero } from '@ethersproject/constants';
import { formatEther, parseEther } from '@ethersproject/units';
import { useWeb3React } from '@web3-react/core';
import { useContext, useState } from 'react';
import NavLogo from '../../assets/logos/SpoolLogo';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';
import TextInput from '../../components/TextInput';
import {
    INSUFFICIENT_FUNDS_ERROR,
    UNSTAKE_CONFIRMED,
    UNSTAKE_PENDING,
} from '../../config/constants';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import modalContext from '../../context/modal/modalContext';
import { formatAccount, formatUSD } from '../../helpers/formats';
import useUserBalance from '../../hooks/web3/useUserBalance';
import useUserStakedSpool from '../../hooks/web3/vault/useUserStakedSpool';
import { preventOverflow, sleep, toBigNumber } from '../../web3/utils';

const UnstakeModal: React.FC = () => {
    const { account } = useWeb3React();
    const { openModal, closeModal, setModalData } = useContext(modalContext);
    const { setError } = useContext(errorContext);

    const { userStakedSpool, updateUserStakedSpool } = useUserStakedSpool();

    const { interfaceSDK, chainConfig } = useContext(appContext);

    const [amount, setAmount] = useState('');

    const userBalance = useUserBalance(chainConfig.spoolToken);

    const onClose = () => {
        closeModal('unstakeModal');
        setAmount('');
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value));
    };

    const handleMaxInput = () => {
        if (!userStakedSpool) return;

        setAmount(userStakedSpool.toString());
    };

    const unstake = async () => {
        try {
            if (!interfaceSDK || !userStakedSpool) return;

            if (toBigNumber(userStakedSpool.toString()).lt(toBigNumber(amount))) {
                openModal('errorModal');
                setModalData((prev) => ({
                    ...prev,
                    message: INSUFFICIENT_FUNDS_ERROR,
                }));
                return;
            }

            setModalData((prev) => ({ ...prev, message: UNSTAKE_PENDING }));
            openModal('waitingModal');

            const stakingContract =
                await interfaceSDK.get.getSpoolStakingContractInstance();

            const unstake = await stakingContract.unstake(parseEther(amount));

            const receipt = await unstake.wait();

            await sleep(5000);

            await updateUserStakedSpool(userStakedSpool);

            setModalData((prev) => ({
                ...prev,
                status: UNSTAKE_CONFIRMED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
            onClose();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal modalName='unstakeModal' className='unstake-modal'>
            <CustomModalHeader title='Unstake' onClose={onClose} />
            <p className='unstake-warning-text'>
                <span>Warning:</span> By unstaking any number of $SPOOL tokens, you will
                lose all your currently accumulated voSPOOL. Any remaining staked $SPOOL
                will begin to accumulate voSPOOL as if you had staked them when your
                unstake transaction is mined.
            </p>
            <div className='content'>
                <div className='staking-detail'>
                    <div className='field-container from'>
                        <span className='field'>From</span>
                        <span>$SPOOL Staking Vault</span>
                        <span className='address'>
                            {formatAccount(chainConfig.staking || AddressZero)}
                        </span>
                    </div>
                    <div className='field-container staked'>
                        <span className='field'>Staked</span>
                        <span>{formatUSD(userStakedSpool || 0)}</span>
                    </div>
                    <div className='field-container to'>
                        <span className='field'>To</span>
                        <span>{formatAccount(account || AddressZero)}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>$SPOOL in wallet</span>
                        <span>
                            {formatUSD(userBalance ? formatEther(userBalance) : 0)}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>Input</span>
                        <div className='input-container'>
                            <TextInput
                                onChange={handleChange}
                                value={amount}
                                name='amount'
                                placeholder='0.00'
                            />
                            <Button onClick={handleMaxInput} variant='secondary'>
                                MAX
                            </Button>
                        </div>
                    </div>
                    <div className='field-container token'>
                        <div className='logo'>
                            <NavLogo />
                        </div>
                        <span className='token-name'>$SPOOL</span>
                    </div>
                </div>
                <Button
                    onClick={unstake}
                    disabled={!amount || parseEther(amount).isZero()}
                >
                    Unstake
                </Button>
            </div>
        </CustomModal>
    );
};

export default UnstakeModal;
